import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import { Button, Modal, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { alpha, styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import ErrorSnackbarAlert from '../../common/ErrorHandling/ErrorSnackbarAlert';
import { styles } from '../../style/style.js';
import EnhancedMaintenanceTableRow from './EnhancedMaintenanceTableRow';

//creates a data object for a single row 
function createData(companyName, bac, fan) {
  return {
    companyName,
    bac,
    fan,
  };
}

// **********************Sort Functions**********************
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  let sorted = stabilizedThis.map((el) => el[0]);
  return sorted;
}

//stying component for each table cell
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.common.black,
    fontSize: 16,
    width: 0,
    fontWeight: 'bold',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
  },
}));

// **********************Table Header**********************
// includes sorting and labeling
function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const ApiList = useSelector((state) => state.apiList.apiList);



  const intl = useIntl();

  //list which defines properties of each table header 
  const headCells = [
    {
      id: 'companyName',
      numeric: false,
      disablePadding: true,
      label: intl.formatMessage({ id: "maintenanceTable.company", defaultMessage: 'Company' })
    },
    {
      id: 'bac',
      numeric: true,
      disablePadding: true,
      label: intl.formatMessage({ id: "maintenanceTable.bac", defaultMessage: 'Business Associated Code (BAC)' })
    },
    {
      id: 'fan',
      numeric: true,
      disablePadding: true,
      label: intl.formatMessage({ id: "maintenanceTable.fan", defaultMessage: 'Fleet Account Number (FAN)' })
    },
    {
      id: 'edit',
      numeric: false,
      disablePadding: false,
      label: ''
    }
  ];


  return (
    <TableHead>
      <TableRow>
        <StyledTableCell padding="checkbox">
        </StyledTableCell>
        {headCells.map((headCell) => (
          <StyledTableCell
            id={headCell.id+"Header"}
            key={headCell.id}
            align='left'
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              aria-label='Hidden' 
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// properties used inside the table header component
EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = (props) => {
  const { numSelected, handleAdd, handleDownload, handleModal, readOnly } = props;
  const ApiList = useSelector((state) => state.apiList.apiList);


  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}
    >
      {
        <Stack direction="row">
          {readOnly == null ? (
            <IconButton id="addButton" aria-label='Add Row' onClick={handleAdd}><AddIcon /></IconButton>
          ) : (
            <IconButton id="addButtonDisabled" aria-label='Add Row: disabled' disabled><AddIcon /></IconButton>
          )
          }
          <IconButton id="exportTableButton" aria-label='Download files' onClick={handleDownload}><DownloadIcon /></IconButton>
          {numSelected > 0 && readOnly === null ? (
            <Tooltip title="Delete">
              <IconButton id="deletRowButton" aria-label='Delete Row' onClick={handleModal}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Delete">
              <IconButton id="deletRowButtonDisabled" aria-label='Delete Row: disabled' disabled>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      }

      {numSelected > 0 ? (
        <Typography
          id="selectedAmtText"
          sx={{ flex: '1 1 100%', paddingLeft: 2 }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
        </Typography>
      )}


    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
  handleAdd: PropTypes.func.isRequired,
  handleDownload: PropTypes.func.isRequired,
  handleModal: PropTypes.func.isRequired,
  readOnly: PropTypes.array
};

//**********************Parent Table Component**********************
// renders toolbar, header, and all row components with data
export default function EnhancedTable() {
  const intl = useIntl();
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('calories');
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [selected, setSelected] = React.useState([]);
  const [readOnly, setReadOnly] = React.useState(null);
  const [data, setData] = React.useState([]);
  const [add, setAdd] = React.useState(false);
  const [alert, setAlert] = React.useState();
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [modal, setModal] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const ApiList = useSelector((state) => state.apiList.apiList);
  const idToken = localStorage.getItem("idToken")



  const [errorMessage, setErrorMessage] = React.useState("");
  const [errorType, setErrorType] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const handleAlertClose = (reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };



  // determines which alert to show after all the data is set
  /*  function showAlert() { */
  // grabs the alert status from local storage and sets the status accordingly
  /* let alertStatus = window.localStorage.getItem('alert');
  window.localStorage.setItem('alert', '');
  if (alertStatus !== '') {
    setAlertOpen(true);
  }
  setAlert(alertStatus);
} */

  //accepts a response from the API and maps out a list of data objects that each row can render
  function setRows(tableData) {
    let dataRows = [];
    tableData.map((dataRow) => {
      dataRows.push(
        createData(dataRow.companyName, parseInt(dataRow.bac), parseInt(dataRow.fan))
      );
    });
    setData(dataRows);
  }

  //grabs all the data needed for the table
  function getAllData() {
    fetch(ApiList.getallbac, {
      method: 'GET',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'guid': `${ApiList.guid}`
      },

    }).then(function (response) {
      return response.json();
    }).then(bacData => {
      console.log(bacData)
      setRows(bacData.bacFanAsscn);
    }).catch((err) => {
      console.log(err);
      /* window.localStorage.setItem('alert', 'error');
      setMessage("There was trouble accessing the data"); */
      setOpen(true)
      setErrorType('error')
      setErrorMessage(intl.formatMessage({ id:err.message.toString().toUpperCase().replace(/ /g,"_"), defaultMessage: err.message }))
      /* setErrorMessage("There was trouble accessing the data") */
    });
  }

  //calls fetch to get all data and shows relevant alert every time table is rendered
  useEffect(() => {
    getAllData();
    /* showAlert(); */
  }, [])

  const handleAddButton = (event, name) => {
    setReadOnly("");
    setAdd(!add);
    setReadOnly('');
  }

  const closeAddRow = () => {
    setReadOnly(null);
    setAdd(false);
    setReadOnly(null);
  }

  // called when the download button at the top of the table is pressed
  function downloadXls() {
    fetch(ApiList.xls, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/octet-stream',
        'guid': `${ApiList.guid}`
      },

    }).then(function (response) {
      return response.blob();
    }).then(blob => {
      let filename = "BacFanTable.xls";
      let a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
    }).catch(err => console.log(err));
  }

  // grabs table sorting states and sets the order in which the table is sorted
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  //this function is passed through
  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    console.log(newSelected);
    setSelected(newSelected);
  };

  const handleReadOnly = (event, name) => {
    setReadOnly(name);
  };

  const handleClose = (event, name) => {
    setReadOnly(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleAlert = (event) => {
    setAlertOpen(false);
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleModal = () => {
    setModal(true);
  }

  const handleModalClose = () => {
    setModal(false);
  }


  const deleteRowRecord = () => {

    let bacList = [];
    data.map((dataRow) => {
      if (selected.includes(dataRow.companyName + dataRow.bac)) {
        bacList.push(
          {
            bacFanAsscn: {
              bac: dataRow.bac,
              fan: dataRow.fan,
              companyName: dataRow.companyName
            }
          }
        );
      }
    });
    console.log(JSON.stringify(bacList));

    const config = {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'guid': `${ApiList.guid}`
      },
      body: JSON.stringify(bacList)
    };

    fetch(ApiList.deletebac, config)
      .then(res => {
        if (res.ok) {
          /* window.localStorage.setItem('alert', 'success'); */
          setModal(false)
          setOpen(true)
          setErrorMessage(intl.formatMessage({ id:'SUCCESS', defaultMessage: "Error Found" }))
          setErrorType('success')

          window.location.reload();
        } else {
          /* window.localStorage.setItem('alert', 'error'); */
          setOpen(true)
          setErrorType('error')
          setErrorMessage('Error')
        }
      })
      .catch((error) => {
        /*  window.localStorage.setItem('alert', 'error'); */
        setOpen(true)
        setErrorType('error')
        setErrorMessage(intl.formatMessage({ id:error.message.toString().toUpperCase().replace(/ /g,"_"), defaultMessage: error.message }))
        window.location.reload();
      })
  }

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  return (
    <Box sx={{ width: '100%', alignItems: 'center' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Modal
          open={modal}
          onClose={handleModalClose}
          aria-labelledby="modal-modal-title"
          header='Delete Confirmation'
          aria-describedby="modal-modal-description"
        >
          <Box sx={styles.modalSx}>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              <WarningIcon fontSize='small' /> <FormattedMessage id='maintenanceTable.deleteMessage' defaultMessage='The following item(s) will be deleted: ' /> <strong>{selected.join(", ")}</strong>
            </Typography>
            <Stack direction='row' spacing={2} sx={{ paddingTop: 5 }}>
              <Button sx={styles.deleteCancel} onClick={handleModalClose}>No, Cancel</Button>
              <Button sx={styles.deleteConfirm} onClick={deleteRowRecord}>Yes, Confirm</Button>
            </Stack>
          </Box>
        </Modal>
        <EnhancedTableToolbar numSelected={selected.length} handleAdd={handleAddButton} handleDownload={downloadXls} handleModal={handleModal} readOnly={readOnly} />
        <TableContainer>
          <Table
            id="BacToFanMaintTable"
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={data.length}
              closeAdd={closeAddRow}
            />
            <TableBody>
              {add && <EnhancedMaintenanceTableRow
                key='add'
                row={createData('', '', '')}
                index={0}
                selected={selected}
                handleClickEvent={handleClick}
                handleClose={handleClose}
                handleEdit={handleReadOnly}
                readOnly={''}
                add={add}
                closeAdd={closeAddRow}
                setErrorMessage={setErrorMessage}
                setOpen={setOpen}
                setErrorType={setErrorType}
              />}

              {stableSort(data, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                    <EnhancedMaintenanceTableRow
                      key={index}
                      row={row}
                      index={index}
                      readOnly={readOnly}
                      selected={selected}
                      handleClickEvent={handleClick}
                      handleEdit={handleReadOnly}
                      handleClose={handleClose}
                      add={add}
                      closeAdd={closeAddRow}
                      setErrorMessage={setErrorMessage}
                      setOpen={setOpen}
                      setErrorType={setErrorType}
                      aria-label='Company'
                    /> 

                  )
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          SelectProps={{ MenuProps: { disableScrollLock: true } }}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      {/* {message !== '' ? (
        <ToastNotification type={alert} handleAlert={handleAlert} open={alertOpen} message={message} />
      ) : (
        <Box></Box>
      )} */}

      {errorMessage !== '' ? (
        <ErrorSnackbarAlert errorType={errorType} open={open} handleClose={handleAlertClose} errorMessage={errorMessage} />
      ) : (
        <Box></Box>
      )}

    </Box>
  );
}
